import React, { useState } from 'react';
import { useOptimizedImage } from '../hooks/useOptimizedImage';
import { getAethermapLogoUrl } from '../utils/imageUtils';
import '../styles/OptimizedImage.css';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  onLoad?: () => void;
  onError?: () => void;
  dataAttributes?: Record<string, string>;
  onDragOver?: (e: React.DragEvent<HTMLImageElement>) => void;
  onDragLeave?: (e: React.DragEvent<HTMLImageElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLImageElement>) => void;
  withCardOverlay?: boolean;
  simpleFallback?: boolean;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  style,
  onClick,
  onLoad,
  onError,
  dataAttributes = {},
  onDragOver,
  onDragLeave,
  onDrop,
  withCardOverlay,
  simpleFallback
}) => {
  const { imgRef } = useOptimizedImage({
    src,
    alt,
    className,
    onLoad,
    onError,
    withCardOverlay
  });
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    if (simpleFallback) {
      return (
        <img
          src={getAethermapLogoUrl()}
          alt="Default"
          style={{ width: 50, height: 50, borderRadius: '15%', ...style }}
          className={className}
          {...dataAttributes}
        />
      );
    }
    // Fallback overlay (AeLogo, gradient border, no loading text)
    return (
      <div
        className={`optimized-image-fallback ${className}`}
        style={{
          ...style,
          position: 'absolute',
          top: 0, left: 0, width: '100%', height: '100%',
          background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.75) 0%, rgba(22, 33, 62, 0.75) 50%, rgba(15, 52, 96, 0.75) 100%)',
          zIndex: 2,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          border: '2.5px solid',
          borderImage: 'linear-gradient(90deg, #7fbcff 0%, #b388ff 100%) 1',
          borderRadius: 'inherit',
          backdropFilter: 'blur(2px)',
          opacity: 1
        }}
        {...dataAttributes}
      >
        <img src={getAethermapLogoUrl()} alt="Default" style={{ width: 50, height: 50, marginTop: '20%', marginBottom: 16, borderRadius: '15%' }} />
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={`optimized-image ${className}`}
      style={style}
      onClick={onClick}
      onLoad={onLoad}
      onError={e => {
        setHasError(true);
        onError?.();
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      {...dataAttributes}
    />
  );
};

export default OptimizedImage; 